import { assistantApi, type AssistantInsight } from '@shared/api-client';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowRight,
  CircleAlert,
  Lightbulb,
  Sparkles,
  TrendingUp,
} from 'lucide-react';
import { useAssistant } from '@/components/assistant/assistant-context';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { HelpTooltip } from '@/components/ui/help-tooltip';
import { cn, formatRelative } from '@/lib/utils';

const toneConfig = {
  positive: {
    icon: TrendingUp,
    badge: 'Positive change',
    badgeTone: 'success' as const,
    accent: 'bg-positive',
  },
  attention: {
    icon: CircleAlert,
    badge: 'Needs attention',
    badgeTone: 'critical' as const,
    accent: 'bg-critical',
  },
  opportunity: {
    icon: Lightbulb,
    badge: 'Opportunity',
    badgeTone: 'warning' as const,
    accent: 'bg-warning',
  },
};

const Finding = ({ insight }: { insight: AssistantInsight }) => {
  const assistant = useAssistant();
  const tone = toneConfig[insight.tone];
  const Icon = tone.icon;
  const ask = async () => {
    assistant.open();
    await assistant.send(
      `Investigate this daily finding and show me the supporting breakdowns: ${insight.title}`,
    );
  };

  return (
    <Card className="relative overflow-hidden shadow-none">
      <div className={cn('absolute inset-y-0 left-0 w-1', tone.accent)} />
      <div className="flex flex-col gap-4 px-5 py-5 sm:flex-row sm:items-center sm:px-6">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <span className="mt-0.5 grid size-9 shrink-0 place-items-center rounded-full bg-surface-muted text-ink">
            <Icon className="size-4" />
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone={tone.badgeTone}>{tone.badge}</Badge>
              <span className="text-xs text-ink-muted">
                {formatRelative(insight.createdAt)}
              </span>
              <HelpTooltip label="About this finding">
                Generated once daily from a deterministic metric change. AI
                writes the concise explanation, while evidence values come
                directly from GainSignal analytics. Confidence:{' '}
                {insight.confidence}.
              </HelpTooltip>
            </div>
            <h2 className="mt-2 text-base font-semibold text-ink">
              {insight.title}
            </h2>
            <p className="mt-1 max-w-3xl text-sm text-ink-muted">
              {insight.summary}
            </p>
            <dl className="mt-3 flex flex-wrap gap-x-5 gap-y-2">
              {insight.evidence.slice(0, 3).map((item) => (
                <div key={item.label} className="flex items-baseline gap-1.5">
                  <dt className="text-xs text-ink-muted">{item.label}</dt>
                  <dd className="text-sm font-semibold text-ink">
                    {item.url ? (
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noreferrer"
                        className="underline decoration-border underline-offset-4 hover:decoration-ink"
                      >
                        {item.value}
                      </a>
                    ) : (
                      item.value
                    )}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
        <Button
          type="button"
          variant="secondary"
          className="shrink-0 self-start sm:self-center"
          onClick={() => void ask()}
        >
          Ask AI
          <ArrowRight className="size-4" />
        </Button>
      </div>
    </Card>
  );
};

export const FindingsFeed = ({ projectId }: { projectId: string }) => {
  const query = useQuery({
    queryKey: ['assistant', 'insights', projectId],
    queryFn: () => assistantApi.listInsights(projectId),
    enabled: Boolean(projectId),
    staleTime: 5 * 60_000,
    refetchInterval: 5 * 60_000,
  });
  const insight = query.data?.insights[0];
  if (!insight) {
    return null;
  }
  return (
    <section aria-label="Daily finding">
      <div className="mb-2 flex items-center gap-2 px-1 text-xs font-medium text-ink-muted">
        <Sparkles className="size-3.5" />
        Daily finding
      </div>
      <Finding insight={insight} />
    </section>
  );
};
